import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CreditCard, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useLanguage, t, API } from './Common';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const assessment = location.state?.assessment;
  const fromOtherDevice = location.state?.fromOtherDevice;

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [basePrice, setBasePrice] = useState(400);
  const [finalPrice, setFinalPrice] = useState(400);
  const [loading, setLoading] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [paymentOption, setPaymentOption] = useState('now');
  const [paymentMethod, setPaymentMethod] = useState('stripe'); // 'stripe' or 'paypal'
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState('user_choice'); // from settings

  useEffect(() => {
    // Allow access if coming from assessment OR from "Other" device selection
    if (!assessment && !fromOtherDevice) {
      navigate('/');
      return;
    }
    fetchSettings();
  }, [assessment, fromOtherDevice]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      const price = response.data.consultation_price;
      setBasePrice(price);
      setFinalPrice(price);
      const method = response.data.payment_method || 'user_choice';
      setAvailablePaymentMethods(method);
      // Set default payment method based on settings
      if (method === 'stripe') {
        setPaymentMethod('stripe');
      } else if (method === 'paypal') {
        setPaymentMethod('paypal');
      }
      setSettingsLoaded(true);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setSettingsLoaded(true);
    }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;

    try {
      const response = await axios.get(`${API}/coupons/validate/${couponCode}`);
      const coupon = response.data;
      setAppliedCoupon(coupon);

      let discountedPrice = basePrice;
      if (coupon.discount_type === 'percentage') {
        discountedPrice = basePrice * (1 - coupon.discount_value / 100);
      } else {
        discountedPrice = Math.max(0, basePrice - coupon.discount_value);
      }

      setFinalPrice(discountedPrice);
      toast.success(t('couponApplied', language));
    } catch (error) {
      toast.error(t('invalidCoupon', language));
      setAppliedCoupon(null);
      setFinalPrice(basePrice);
    }
  };

  const handlePayment = async () => {
    if (paymentOption === 'later') {
      if (!email) {
        toast.error(language === 'it' ? 'Email obbligatoria per prenotare' : 'Email required to book');
        return;
      }
      navigate('/book-appointment', { state: { assessment, email, name, payLater: true, fromOtherDevice } });
      return;
    }

    setLoading(true);

    try {
      const originUrl = window.location.origin;
      const checkoutRequest = {
        assessment_id: assessment?.id || 'other-device',
        email: email || undefined,
        coupon_code: appliedCoupon?.code || undefined,
        origin_url: originUrl
      };

      let response;
      if (paymentMethod === 'paypal') {
        // PayPal checkout
        response = await axios.post(`${API}/payments/paypal/checkout`, checkoutRequest);
      } else {
        // Stripe checkout
        response = await axios.post(`${API}/payments/checkout`, checkoutRequest);
      }
      
      if (response.data.url) {
        // Create a temporary link and click it - works better on mobile browsers
        const link = document.createElement('a');
        link.href = response.data.url;
        link.setAttribute('target', '_self');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Fallback after small delay if link click didn't work
        setTimeout(() => {
          if (document.visibilityState !== 'hidden') {
            window.location.href = response.data.url;
          }
        }, 500);
      } else {
        toast.error(language === 'it' ? 'Errore: URL di pagamento non ricevuto' : 'Error: Payment URL not received');
        setLoading(false);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(language === 'it' ? 'Errore durante l\'inizializzazione del pagamento. Riprova.' : 'Payment initialization failed. Please try again.');
      setLoading(false);
    }
  };

  if (!settingsLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">{t('processing', language)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6">
      <div className="scan-line" />
      <LanguageSwitcher />
      <div className="max-w-2xl mx-auto py-12">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">{t('bookAppointment', language)}</h1>
        </div>

        <div className="glass-panel rounded-sm p-8 md:p-12 border-2 border-premium premium-glow">
          <div className="mb-8 text-center">
            <p className="text-sm font-mono uppercase tracking-widest text-premium mb-2">
              {t('consultationFee', language)}
            </p>
            {appliedCoupon && (
              <p className="text-2xl line-through text-muted-foreground mb-1" data-testid="original-price">
                € {basePrice.toFixed(2)}
              </p>
            )}
            <p className="text-5xl font-bold text-premium" data-testid="final-price">
              € {finalPrice.toFixed(2)}
            </p>
            {appliedCoupon && (
              <p className="text-sm text-primary mt-2" data-testid="coupon-info">
                Coupon "{appliedCoupon.code}" applied - 
                {appliedCoupon.discount_type === 'percentage'
                  ? ` ${appliedCoupon.discount_value}% off`
                  : ` €${appliedCoupon.discount_value} off`}
              </p>
            )}
          </div>

          <div className="mb-8">
            <label className="block text-sm font-mono uppercase tracking-widest text-muted-foreground mb-4">
              {language === 'it' ? 'Modalità di Pagamento' : 'Payment Option'}
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setPaymentOption('now')}
                data-testid="pay-now-option"
                className={`p-4 rounded-sm border transition-all duration-300 ${
                  paymentOption === 'now'
                    ? 'border-primary bg-primary/20 text-primary'
                    : 'border-white/20 hover:border-primary/50'
                }`}
              >
                <CreditCard className="w-6 h-6 mx-auto mb-2" />
                <p className="text-sm font-mono uppercase">
                  {language === 'it' ? 'Paga Ora' : 'Pay Now'}
                </p>
              </button>
              <button
                onClick={() => setPaymentOption('later')}
                data-testid="pay-later-option"
                className={`p-4 rounded-sm border transition-all duration-300 ${
                  paymentOption === 'later'
                    ? 'border-primary bg-primary/20 text-primary'
                    : 'border-white/20 hover:border-primary/50'
                }`}
              >
                <CreditCard className="w-6 h-6 mx-auto mb-2" />
                <p className="text-sm font-mono uppercase">
                  {language === 'it' ? 'Paga Durante Incontro' : 'Pay During Meeting'}
                </p>
              </button>
            </div>
          </div>

          {/* Payment Method Selection - only show when "Pay Now" is selected */}
          {paymentOption === 'now' && availablePaymentMethods === 'user_choice' && (
            <div className="mb-8">
              <label className="block text-sm font-mono uppercase tracking-widest text-muted-foreground mb-4">
                {language === 'it' ? 'Metodo di Pagamento' : 'Payment Method'}
              </label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setPaymentMethod('stripe')}
                  data-testid="stripe-method"
                  className={`p-4 rounded-sm border transition-all duration-300 ${
                    paymentMethod === 'stripe'
                      ? 'border-primary bg-primary/20 text-primary'
                      : 'border-white/20 hover:border-primary/50'
                  }`}
                >
                  <div className="text-2xl mb-2">💳</div>
                  <p className="text-sm font-mono uppercase">
                    {language === 'it' ? 'Carta di Credito' : 'Credit Card'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Stripe</p>
                </button>
                <button
                  onClick={() => setPaymentMethod('paypal')}
                  data-testid="paypal-method"
                  className={`p-4 rounded-sm border transition-all duration-300 ${
                    paymentMethod === 'paypal'
                      ? 'border-primary bg-primary/20 text-primary'
                      : 'border-white/20 hover:border-primary/50'
                  }`}
                >
                  <div className="text-2xl mb-2">🅿️</div>
                  <p className="text-sm font-mono uppercase">PayPal</p>
                  <p className="text-xs text-muted-foreground mt-1">{language === 'it' ? 'Conto PayPal' : 'PayPal Account'}</p>
                </button>
              </div>
            </div>
          )}

          <div className="space-y-6 mb-8">
            <div>
              <label className="block text-sm mb-2">
                {t('optionalInfo', language)}
              </label>
            </div>

            <div>
              <label className="block text-sm mb-2">
                {t('email', language)} {paymentOption === 'later' && <span className="text-secondary">*</span>}
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="email-input"
                placeholder="your@email.com"
                className="bg-transparent border-b border-white/20 focus:border-primary outline-none rounded-none px-0 py-4 text-lg transition-colors"
                required={paymentOption === 'later'}
              />
            </div>

            <div>
              <label className="block text-sm mb-2">{t('name', language)}</label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="name-input"
                placeholder="Mario Rossi"
                className="bg-transparent border-b border-white/20 focus:border-primary outline-none rounded-none px-0 py-4 text-lg transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm mb-2">{t('couponCode', language)}</label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  data-testid="coupon-input"
                  placeholder="DISCOUNT10"
                  disabled={paymentOption === 'later'}
                  className="bg-transparent border-b border-white/20 focus:border-primary outline-none rounded-none px-0 py-4 text-lg transition-colors disabled:opacity-50"
                />
                <Button
                  onClick={applyCoupon}
                  disabled={!couponCode.trim() || paymentOption === 'later'}
                  data-testid="apply-coupon-button"
                  className="rounded-none border border-primary bg-primary/10 text-primary hover:bg-primary hover:text-black transition-all duration-300 uppercase tracking-widest font-mono text-xs px-6 disabled:opacity-50"
                >
                  {t('applyCoupon', language)}
                </Button>
              </div>
              {paymentOption === 'later' && (
                <p className="text-xs text-muted-foreground mt-2">
                  {language === 'it' 
                    ? 'I coupon non sono disponibili per il pagamento durante l\'incontro' 
                    : 'Coupons not available for pay later option'}
                </p>
              )}
            </div>
          </div>

          <Button
            onClick={handlePayment}
            disabled={loading || (paymentOption === 'later' && !email)}
            data-testid="proceed-payment-button"
            className="w-full h-14 rounded-none border border-premium bg-premium/10 text-premium hover:bg-premium/30 transition-all duration-300 uppercase tracking-widest font-mono text-sm mb-4 disabled:opacity-50"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            {loading 
              ? (language === 'it' 
                  ? `Reindirizzamento a ${paymentMethod === 'paypal' ? 'PayPal' : 'Stripe'}...` 
                  : `Redirecting to ${paymentMethod === 'paypal' ? 'PayPal' : 'Stripe'}...`) 
              : paymentOption === 'later'
                ? (language === 'it' ? 'Prenota Appuntamento' : 'Book Appointment')
                : t('proceedPayment', language)
            }
          </Button>
          
          {loading && (
            <p className="text-center text-sm text-muted-foreground mb-4 animate-pulse">
              {language === 'it' 
                ? `Attendere, stai per essere reindirizzato a ${paymentMethod === 'paypal' ? 'PayPal' : 'Stripe'}...` 
                : `Please wait, you are being redirected to ${paymentMethod === 'paypal' ? 'PayPal' : 'Stripe'}...`}
            </p>
          )}

          <Button
            variant="outline"
            onClick={() => navigate(fromOtherDevice ? '/device-selection' : '/results', { state: { assessment } })}
            data-testid="back-to-results-button"
            className="w-full border-white/20 hover:border-primary/50 rounded-none uppercase tracking-widest font-mono text-xs"
          >
            {t('back', language)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;